import { CardStore } from '@intelingu/core/stores/CardStore';
import { autobind } from 'core-decorators';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Component } from 'react';
import { COMMON_NS } from '@intelingu/core/constants/base.constants';
import { EvaluationMark } from '@intelingu/core/interfaces/models/CardModel';
import { lazyInject } from '@intelingu/core/IoC/container';
import { VocabularyCardStore } from '@intelingu/core/stores/cards/VocabularyCardStore';
import withTranslation from '@intelingu/core/utils/withTranslation';
import { FormattedString } from '../../../partials/FormattedString';
import { SentenceTitle } from './SentenceTitle';
import {AppStore} from "../../../../stores/AppStore";
import {DisabledContent} from "../DisabledContent";

export interface VocabularyAnswerProps {
    t?;
}

@withTranslation(COMMON_NS)
@observer export class VocabularyAnswer extends Component<VocabularyAnswerProps, any> {

    @lazyInject(VocabularyCardStore) vocabularyCardStore: VocabularyCardStore;
    @lazyInject(CardStore) cardStore: CardStore;
    @lazyInject(AppStore) appStore: AppStore;

    @computed get card() {
        return this.vocabularyCardStore.card;
    }

    evaluationButtons = [
        { className: 'red', title: 'trainingAgainButton', value: EvaluationMark.AGAIN },
        { className: 'orange', title: 'trainingHeavyButton', value: EvaluationMark.HARD },
        { className: 'blue', title: 'trainingWellButton', value: EvaluationMark.GOOD },
        { className: 'green', title: 'trainingEasyButton', value: EvaluationMark.EASY },
    ];

    @computed get filteredButtons() {
        if (this.card.cycle && this.card.cycle > 1) {
        // if (this.vocabularyCardStore.testOdd) {
            return this.evaluationButtons;
        }

        return this.evaluationButtons.filter(button => [
            EvaluationMark.AGAIN, EvaluationMark.GOOD
        ].includes(button.value));
    }

    @observable isMnemoVisible = false;

    @action.bound toggleMnemo() {
        this.isMnemoVisible = !this.isMnemoVisible;
    }

    _renderDisabledBlock() {
        return <DisabledContent />;
    }

    @autobind _renderMnemo() {
        const { t } = this.props;
        const { card } = this.vocabularyCardStore;

        if (card.derivable_from_words) {
            return <div className={'mnemo tour-mnemo'}>
                <h1>Das kennst du schon...</h1>

                { card.derivable_from_words === 'disabled' ? (
                    <div className={'keywords'}>
                        { this._renderDisabledBlock() }
                    </div>
                ) : (
                    <FormattedString className={'keywords'} value={card.derivable_from_words} />
                )}
            </div>
        }

        if (!card.mnemo) {
            return null;
        }

        const { short_sentence, sentence, disabled } = card.mnemo;

        return <div className={'mnemo'}>
            <h1 className={'tour-mnemo-title'}>{ t('trainingMnemo').toUpperCase() }</h1>

            { disabled ? (
                <div className={'keywords'}>
                    { this._renderDisabledBlock() }
                </div>
            ) : (
                <>
                    <p className={'keywords tour-mnemo-content'}>
                        <FormattedString value={short_sentence} />
                    </p>

                    <div className={`collapse ${ this.isMnemoVisible ? 'active' : '' }`}>
                        <button onClick={this.toggleMnemo} className={'tour-mnemo-dropdown'}><i className={'ion ion-md-arrow-dropright'} /> { t('trainingMnemoStory') }</button>
                        { this.isMnemoVisible &&
                            <FormattedString className={'content'} value={sentence} />
                        }
                    </div>
                </>
            ) }
        </div>
    }

    @autobind _renderInfo() {
        const { t } = this.props;

        if (!this.card.info) {
            return null;
        }

        return <div className={'sentences'} style={{ margin: 0 }}>
            <div className={'sentence'}>
                <h1>{ t('trainingInfo') }</h1>
                {/*<div className={'subtitle'} dangerouslySetInnerHTML={{ __html: this.card.info }} />*/}
                <div className={'subtitle'}>
                    { this.card.info === 'disabled' ? (
                        this._renderDisabledBlock()
                    ) : <FormattedString value={this.card.info} /> }
                </div>
            </div>
        </div>
    }

    @autobind _renderEvaluationButtons() {
        const { t } = this.props;

        return <div className={'bottom-controls tour-answers'} style={{ opacity: this.cardStore.isBufferLoaded ? 1 : 0.4 }}>
            { this.filteredButtons.map(({ className, title, value }) =>
                <button
                    className={className}
                    key={value}
                    onClick={() => this.vocabularyCardStore.evaluate(value)}
                >{ t(title) }</button>
            )}
        </div>
    }

    componentDidMount(): void {
        // this.appStore.tourContinue();

        document.addEventListener ("keydown", this.hotKeyListener);
    }

    componentWillUnmount(): void {
        document.removeEventListener('keydown', this.hotKeyListener);
    }

    @autobind hotKeyListener(event) {
        if (['1', '2', '3', '4'].includes(event.key)) {
            const buttonKey = this.card.cycle && this.card.cycle < 2 && parseInt(event.key) > 1 ? EvaluationMark.GOOD  : parseInt(event.key);

            if (this.filteredButtons.filter(i => i.value === buttonKey).length) {
                this.vocabularyCardStore.evaluate(buttonKey);

                this.removeHotKeyListener();
            }
        }

        if (event.code === 'Enter') {
            this.vocabularyCardStore.evaluate(EvaluationMark.GOOD);

            this.removeHotKeyListener();
        }
    }

    render() {
        const { card } = this.vocabularyCardStore;

        return <>
            {/*<div className={'sep'} />*/}

            <div className={'mt-3'}>
                <span className={'vocabulary-word tour-translation'}>{ this.vocabularyCardStore.translationWord }</span>

                { /*!!card.inverted && (
                    <div className={'vocabulary-source-word'}>
                        <SoundComponent
                            autoplay={true}
                            url={card.word_translation.audio}
                        />
                    </div>
                )*/}
            </div>

            <div className={'sentences'}>
                { card.example_sentences.map((sentence, index) =>
                    <SentenceTitle
                        sentence={sentence}
                        index={index}
                        key={index}
                        title={`ENHANCER ${index + 1}`}
                    />
                )}
            </div>

            { this._renderMnemo() }
            { this._renderInfo() }
            { this._renderEvaluationButtons() }
        </>
    }

    private removeHotKeyListener() {
        document.removeEventListener('keydown', this.hotKeyListener);
    }

}
